import type { Theme } from '@mui/material';
import { Box, Stack, Typography, useMediaQuery } from '@mui/material';
import type { FC } from 'react';

import { Button } from '~/components/atoms/Button';
import { ImageSanity } from '~/components/atoms/ImageSanity';
import { PAGE_CONTENT_MAX_WIDTH } from '~/constants';
import { headerSizes } from '~/constants/sanityComponents';
import type { HeroProps } from '~/types/sanity/components';
import { checkAuthVisibility } from '~/utils/auth';
import { getCompoundTestIdGenerator } from '~/utils/getCompoundTestIdGenerator';
import { buildSanityLinkUrl } from '~/utils/link';
import {
  HERO_BANNER_DESKTOP_HEIGHT,
  HERO_BANNER_DESKTOP_WIDTH,
  HERO_BANNER_MOBILE_HEIGHT,
  HERO_BANNER_MOBILE_WIDTH,
} from './constants';

const isTitleSize = (n: number): n is NonNullable<HeroProps['titleSize']> => n >= 1 && n <= 5;

export const Hero: FC<Omit<HeroProps, '_key' | '_type'>> = ({
  links,
  imageDesktopObject,
  imageMobileObject,
  isAuthenticated,
  subtitle,
  tagline,
  textProtectionColor,
  textProtectionOpacity,
  title,
  titleSize = 5,
}) => {
  const smUp = useMediaQuery<Theme>((theme) => theme.breakpoints.up('sm'));
  const getCompoundTestId = getCompoundTestIdGenerator('hero');

  const responsiveTitleSize = smUp ? titleSize : titleSize - 1;

  const allowedTitleSize = isTitleSize(responsiveTitleSize) ? responsiveTitleSize : 1;

  const titleVariant = headerSizes[allowedTitleSize];
  const visibleLinks = links ? links.filter((link) => checkAuthVisibility(link.authVisibility, isAuthenticated)) : [];
  const hasContent = !!(tagline || title || subtitle || visibleLinks.length);

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        justifyContent: 'center',
        aspectRatio: {
          sm: HERO_BANNER_DESKTOP_WIDTH / HERO_BANNER_DESKTOP_HEIGHT,
        },
      }}
    >
      {/* Background images container */}
      <Box sx={{ position: 'absolute', height: '100%', width: '100%', top: 0, left: 0 }}>
        <Box sx={{ display: { xs: 'none', sm: 'inline' } }}>
          <ImageSanity
            imageObject={imageDesktopObject}
            height={HERO_BANNER_DESKTOP_HEIGHT}
            width={HERO_BANNER_DESKTOP_WIDTH}
            testId={getCompoundTestId('desktop')}
          />
        </Box>
        <Box sx={{ display: { xs: 'inline', sm: 'none' } }}>
          <ImageSanity
            imageObject={imageMobileObject}
            height={HERO_BANNER_MOBILE_HEIGHT}
            width={HERO_BANNER_MOBILE_WIDTH}
            testId={getCompoundTestId('mobile')}
          />
        </Box>
      </Box>

      {textProtectionColor && (
        <Box
          sx={{
            position: 'absolute',
            height: '100%',
            width: '100%',
            top: 0,
            left: 0,
            backgroundColor: textProtectionColor,
            opacity: `${(1 / 100) * (textProtectionOpacity ?? 0)}`,
          }}
          data-testid={getCompoundTestId('textProtection')}
        />
      )}

      {/* Content container */}
      {hasContent && (
        <Box
          sx={{
            // to display on top of absolutely positioned images
            position: 'relative',
            maxWidth: PAGE_CONTENT_MAX_WIDTH,
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            marginX: { xs: 2, sm: 5 },
            py: 5,
          }}
          data-testid={getCompoundTestId('content')}
        >
          <Stack sx={{ gap: 2 }}>
            {tagline && (
              <Typography variant="body1" sx={{ maxWidth: ({ spacing }) => ({ xs: '50%', sm: spacing(64) }) }}>
                {tagline}
              </Typography>
            )}
            {title && (
              <Typography variant={titleVariant} sx={{ maxWidth: ({ spacing }) => ({ xs: '50%', sm: spacing(64) }) }}>
                {title}
              </Typography>
            )}
            {subtitle && (
              <Typography
                variant="h6"
                sx={{
                  maxWidth: ({ spacing }) => ({ xs: '60%', sm: spacing(64) }),
                  // we should avoid fontWeight overrides but the storefront design doesn't follow the design guidelines
                  // to be addressed with design once we have capacity
                  fontWeight: 'regular',
                }}
              >
                {subtitle}
              </Typography>
            )}
            {!!visibleLinks.length && (
              <Stack sx={{ flexDirection: 'row', gap: 4, flexWrap: 'wrap' }}>
                {visibleLinks.map((link) => (
                  <Button
                    key={link._key}
                    variant="contained"
                    data-testid={getCompoundTestId('button', link._key)}
                    to={buildSanityLinkUrl(link)}
                    id={link.id}
                  >
                    {link.label}
                  </Button>
                ))}
              </Stack>
            )}
          </Stack>
        </Box>
      )}
    </Box>
  );
};
